import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog.tsx';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import { toast } from 'sonner';
import type { AssignedProperty, CognitoUser } from '@metron/shared';
import {
  defaultRoleForCustomer,
  grantableRoles,
  isPropertyManagerRole,
  propertyManagerRoleForCustomer,
  rolesForCustomer,
} from '@metron/shared';
import { useCustomers } from '../../../hooks/useCustomers.ts';
import { saveIdentityThenAccess } from '../../../lib/user-writes.ts';
import { usePermissions } from '../../../hooks/usePermissions.ts';
import { CustomerRoleStep } from '../../user-form/CustomerRoleStep.tsx';
import { cognitoKey } from '../identity.ts';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('UserEditDialog');

type UserType = 'customer' | 'metron-internal';

/** The assignment read's own state — see `assignmentRead` below. */
type AssignmentRead = 'loading' | 'ready' | 'error';

interface UserEditDialogProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onUpdateUser: (
    userId: string,
    updates: { name?: string; email?: string; enabled?: boolean },
  ) => Promise<CognitoUser>;
  /**
   * When set, the customer is fixed to this id: pre-selected, the picker shown
   * but not interactive, and the user type pinned to "Customer user". Used on
   * the customer detail page, where re-targeting another customer (or making
   * the user Metron-internal) would make the row vanish from the table being
   * looked at. MET-3237 row 13.
   */
  lockedCustomerId?: number;
}

/**
 * The one user edit dialog: a single screen with the user's identity on top and
 * their access below, which also covers the customer-scoped "Edit role" case
 * via `lockedCustomerId`. That prop is the only thing either mount varies, and
 * all it does is disable the fields the mount does not get to change.
 */
export function UserEditDialog(
  { open, user, onOpenChange, onUpdateUser, lockedCustomerId }: UserEditDialogProps,
) {
  const customerLocked = lockedCustomerId != null;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    lockedCustomerId ?? null,
  );
  const [userType, setUserType] = useState<UserType>('customer');
  /**
   * The assignment read is tracked three-valued rather than as a boolean
   * because a failed read must never fall through to the access defaults this
   * component still holds: rendering those as though they had been loaded is
   * exactly what MET-3284 reported. Only `ready` renders the access controls.
   */
  const [assignmentRead, setAssignmentRead] = useState<AssignmentRead>('loading');
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  // Property state — driven by the PM role within CustomerRoleStep
  const [availableProperties, setAvailableProperties] = useState<AssignedProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);

  const {
    customers,
    getUserCustomers,
    updateUserCustomers,
    getCustomerProperties,
    updateUserProperties,
    invalidateUsers,
  } = useCustomers();
  const { isAdmin, isSupport } = usePermissions();

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  // [] until a customer is selected — safe because CustomerRoleStep guards on a
  // selected customer before rendering any role control that consumes this.
  const grantable = useMemo(
    () =>
      selectedCustomer
        ? grantableRoles(
          rolesForCustomer(selectedCustomer.accountType, selectedCustomer.realEstateAccountType),
          { isAdmin, isSupport },
        )
        : [],
    [selectedCustomer, isAdmin, isSupport],
  );

  const isPropertyMgr = isPropertyManagerRole(selectedRole);

  const fetchProperties = (customerId: number) => {
    setAvailableProperties([]);
    setLoadingProperties(true);
    getCustomerProperties(customerId)
      .then(setAvailableProperties)
      .catch((error) => {
        logger.error({ error }, 'Failed to load customer properties');
        toast.error('Failed to load properties');
      })
      .finally(() => setLoadingProperties(false));
  };

  const handleCustomerChange = (customerId: number) => {
    setSelectedCustomerId(customerId);
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      const roles = grantableRoles(
        rolesForCustomer(customer.accountType, customer.realEstateAccountType),
        { isAdmin, isSupport },
      );
      // Auto-select the first grantable role, falling back to the default role
      setSelectedRole(
        roles[0] ?? defaultRoleForCustomer(customer.accountType, customer.realEstateAccountType),
      );
    }

    // Clear property state; if the auto-selected role is PM, fetch will trigger via
    // the role effect below. But since the role and customer are set in the same render
    // cycle we proactively fetch here too when the current role is already PM.
    setSelectedPropertyIds([]);
    setAvailableProperties([]);
  };

  // Fetch properties whenever the selected customer + role combination calls for it.
  useEffect(() => {
    if (selectedCustomerId != null && isPropertyMgr) {
      fetchProperties(selectedCustomerId);
    } else {
      setAvailableProperties([]);
      setSelectedPropertyIds([]);
      setLoadingProperties(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, isPropertyMgr]);

  const handleCustomerClear = () => {
    setSelectedCustomerId(null);
    setSelectedRole(null);
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    // Clear property selection when switching away from PM role
    if (!isPropertyManagerRole(role)) {
      setSelectedPropertyIds([]);
      setAvailableProperties([]);
    }
  };

  const handlePropertyToggle = (propertyId: number, checked: boolean) => {
    setSelectedPropertyIds((prev) =>
      checked ? [...prev, propertyId] : prev.filter((id) => id !== propertyId)
    );
  };

  /**
   * Loads the user's current customer association — the only thing the access
   * section is ever seeded from. Deliberately not seeded from the table row
   * while this is pending or failed: waiting is simpler than reconciling, and
   * an unconfirmed value in an editable control is what MET-3284 is about.
   * Also the Retry handler.
   */
  const loadAssignment = () => {
    if (!user) return;
    // Reset access/type state so nothing carries over from a previously
    // edited user, or from an earlier attempt at this one.
    setUserType('customer');
    setSelectedCustomerId(lockedCustomerId ?? null);
    setSelectedRole(null);
    setAvailableProperties([]);
    setSelectedPropertyIds([]);
    setAssignmentRead('loading');

    // Keyed on the Cognito username, like every other /users/* call: an email
    // does not resolve for a federated (entraid_*) account. See MET-3285.
    getUserCustomers(cognitoKey(user))
      .then((response) => {
        if (response.isPropertyUser) {
          // Property user = customer user whose role is Property Manager.
          // Pre-populate as customer type with PM role + their assigned properties.
          setUserType('customer');
          // The lock wins over the response, so a locked dialog can never
          // silently re-target another customer.
          const seedCustomerId = lockedCustomerId ?? response.properties[0]?.customerId ?? null;
          setSelectedCustomerId(seedCustomerId);
          const seedCustomer = customers.find((c) => c.id === seedCustomerId);
          setSelectedRole(
            propertyManagerRoleForCustomer(
              seedCustomer?.accountType,
              seedCustomer?.realEstateAccountType,
            ),
          );
          setSelectedPropertyIds(response.properties.map((p) => p.id));
          // availableProperties will load via the useEffect above once
          // selectedCustomerId + isPropertyMgr are both set.
        } else {
          // A locked dialog is always editing a customer grant.
          setUserType(
            response.isMetronInternal && !customerLocked ? 'metron-internal' : 'customer',
          );
          const seedCustomerId = lockedCustomerId ?? response.customer?.id ?? null;
          setSelectedCustomerId(seedCustomerId);
          const seedCustomer = customers.find((c) => c.id === seedCustomerId) ??
            response.customer;
          if (seedCustomer) {
            // Pre-populate from the user's current Cognito RBAC group.
            // Fall back to the account-type default when the user has no RBAC group yet.
            setSelectedRole(
              response.rbacRole ??
                defaultRoleForCustomer(
                  seedCustomer.accountType,
                  seedCustomer.realEstateAccountType,
                ),
            );
          }
        }
        setAssignmentRead('ready');
      })
      .catch((error) => {
        logger.error({ error }, 'Failed to load user customers');
        toast.error('Failed to load customer assignment');
        setAssignmentRead('error');
      });
  };

  useEffect(() => {
    if (user && open) {
      setEmail(user.email);
      setName(user.name);
      setEnabled(user.enabled);
      loadAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  const handleUserTypeChange = (type: UserType) => {
    setUserType(type);
    // Reset access state when switching types
    setSelectedCustomerId(null);
    setSelectedRole(null);
    setAvailableProperties([]);
    setSelectedPropertyIds([]);
  };

  // A Metron-internal user has no customer access to fill in.
  const hasAccessSection = userType !== 'metron-internal';

  // Access needs a customer and a grantable role, and — for a PM role — at
  // least one property. `grantable.length === 0` covers both an unmappable
  // account type and a signed-in user who may not grant its roles.
  const accessIncomplete = hasAccessSection &&
    (selectedCustomerId == null || grantable.length === 0 || selectedRole == null ||
      (isPropertyMgr && selectedPropertyIds.length === 0));

  // One form, so one validity check for the whole thing. The identity fields
  // stay editable while the assignment read is loading or failed, but Save does
  // not: the composite save always issues the access write alongside the
  // identity write, with whatever the access state currently holds. Saving
  // before a successful read would therefore write this component's defaults
  // over the user's real assignment — MET-3284.
  const saveDisabled = isLoading || assignmentRead !== 'ready' || !name || !email ||
    accessIncomplete;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }

    // An optimisation, not a correctness requirement: a save that only changed
    // the role should not issue a PUT /users/:id that writes back what Cognito
    // already holds.
    const identityChanged = name !== user.name || email !== user.email ||
      enabled !== user.enabled;

    setIsLoading(true);
    try {
      // Identity first, then access — the users query is invalidated once, after
      // the access write. See lib/user-writes.ts for why the order matters.
      await saveIdentityThenAccess({
        // Update user attributes
        writeIdentity: () =>
          identityChanged
            ? onUpdateUser(cognitoKey(user), {
              name,
              email,
              enabled,
            })
            : Promise.resolve(null),
        writeAccess: () =>
          isPropertyMgr
            // Property-scoped access: update property assignments only.
            // The backend revokes any existing customer grant and sets the PM group.
            ? updateUserProperties(cognitoKey(user), { propertyIds: selectedPropertyIds })
            // Update customer association (include role if set for non-internal)
            : updateUserCustomers(cognitoKey(user), {
              customerId: userType === 'metron-internal' ? null : selectedCustomerId,
              isMetronInternal: userType === 'metron-internal',
              ...(selectedRole != null && userType === 'customer' ? { role: selectedRole } : {}),
            }),
        // Only meaningful when the identity write actually landed; without it
        // there is nothing stale to refetch on the access write's failure path.
        invalidateUsers: identityChanged ? invalidateUsers : undefined,
      });

      toast.success('User updated successfully');
      onOpenChange(false);
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Failed to update user');
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  const lockedCustomer = customerLocked
    ? customers.find((c) => c.id === lockedCustomerId)
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            {lockedCustomer
              ? `Update this user's details, role and properties for ${lockedCustomer.waterscopeAccountName}.`
              : 'Update user information, account status and customer assignment.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col'>
          <div className='grid min-h-0 flex-1 gap-4 overflow-y-auto py-4 pr-1'>
            <div className='grid gap-2'>
              <Label htmlFor='edit-email'>Email</Label>
              <Input
                id='edit-email'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='edit-name'>Full Name</Label>
              <Input
                id='edit-name'
                type='text'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            <div className='flex items-center gap-2'>
              <input
                id='edit-enabled'
                type='checkbox'
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={isLoading}
                className='h-4 w-4 rounded border-gray-300'
              />
              <Label htmlFor='edit-enabled' className='cursor-pointer'>
                Account Enabled
              </Label>
            </div>
            {!enabled && (
              <p className='text-sm text-muted-foreground'>
                Disabled users cannot log in to the system.
              </p>
            )}

            {/* User type toggle */}
            <div className='grid gap-2'>
              <span className='text-sm font-medium'>User Type</span>
              {assignmentRead === 'loading'
                ? (
                  <div className='flex items-center gap-2 py-2 text-sm text-muted-foreground'>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
                    Loading user details...
                  </div>
                )
                : assignmentRead === 'error'
                ? (
                  // No toggle and no defaults here: until the read succeeds we
                  // do not know this user's type, so there is nothing to show.
                  <div className='flex flex-col items-start gap-2 py-2'>
                    <p className='text-sm text-destructive'>
                      Couldn't load this user's customer assignment.
                    </p>
                    <Button type='button' variant='outline' size='sm' onClick={loadAssignment}>
                      Retry
                    </Button>
                  </div>
                )
                : (
                  <>
                    <div
                      role='group'
                      aria-label='User type'
                      className='flex rounded-md border overflow-hidden w-fit'
                    >
                      <button
                        type='button'
                        onClick={() => handleUserTypeChange('customer')}
                        disabled={isLoading || customerLocked}
                        className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          userType === 'customer'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-foreground enabled:hover:bg-accent'
                        }`}
                      >
                        Customer user
                      </button>
                      <button
                        type='button'
                        onClick={() => handleUserTypeChange('metron-internal')}
                        disabled={isLoading || customerLocked}
                        className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          userType === 'metron-internal'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-foreground enabled:hover:bg-accent'
                        }`}
                      >
                        Metron internal
                      </button>
                    </div>
                    {userType === 'metron-internal' && (
                      <p className='text-sm text-muted-foreground'>
                        This user will have access to internal Metron tools and features.
                      </p>
                    )}
                  </>
                )}
            </div>

            {/* Access — hidden for Metron-internal users, who hold no grant */}
            {hasAccessSection && assignmentRead === 'ready' && (
              <div className='border-t pt-4'>
                <CustomerRoleStep
                  customers={customers}
                  selectedCustomerId={selectedCustomerId}
                  onCustomerChange={handleCustomerChange}
                  onCustomerClear={customerLocked ? undefined : handleCustomerClear}
                  customerLocked={customerLocked}
                  selectedRole={selectedRole}
                  onRoleChange={handleRoleChange}
                  grantable={grantable}
                  disabled={isLoading}
                  availableProperties={availableProperties}
                  loadingProperties={loadingProperties}
                  selectedPropertyIds={selectedPropertyIds}
                  onPropertyToggle={handlePropertyToggle}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={saveDisabled}>
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
